import { format, parseISO } from 'date-fns';
import { formatHoursShort } from '../../../../../utils/timesheetTime';
import { BalanceLeaveType, LeaveAccrualRow } from '../../../config';

/** "184.61 h". Two decimals at most; the database keeps four. */
export const formatLeaveHours = (hours: number): string => `${formatHoursShort(hours)} h`;

/** A signed movement on the balance: "+5.77 h", "−7.5 h". */
export const formatSignedHours = (hours: number): string => {
  const rounded = Math.round(hours * 100) / 100;
  if (rounded === 0) return '0 h';
  return `${rounded > 0 ? '+' : '−'}${formatHoursShort(Math.abs(rounded))} h`;
};

/**
 * Hours as days of THIS employee's standard day. Display only: a day is a
 * different number of hours for each person, and changes if their standard day
 * does, which is why nothing is stored in days.
 */
export const formatLeaveDays = (hours: number, hoursPerDay: number): string => {
  if (!hoursPerDay) return '—';
  const days = Math.round((hours / hoursPerDay) * 100) / 100;
  return `${formatHoursShort(days)} ${Math.abs(days) === 1 ? 'day' : 'days'}`;
};

/** A date column from the database ("2026-10-04"), with no time zone to shift it. */
export const formatDay = (date: string): string => format(parseISO(date), 'd MMM yyyy');

export const formatDayShort = (date: string): string => format(parseISO(date), 'd MMM');

/** The calendar year of a database date, read off the text so no time zone can move it. */
export const yearOf = (date: string): number => Number(date.slice(0, 4));

/**
 * The year a dated item is counted in. Mirrors get_employee_leave, which has
 * to put everything the balance counts into SOME year or the years would not
 * add up to the balance: anything dated before the start goes in the first
 * year, and for annual leave anything dated ahead goes in this one. Sick leave
 * dated ahead is left where it is -- next year's sick leave is a different
 * balance. Change both together.
 */
export const countedYear = (
  date: string,
  leaveType: BalanceLeaveType,
  leave: { accrual_start: string | null; current_year: number }
): number => {
  const first = leave.accrual_start ? yearOf(leave.accrual_start) : yearOf(date);
  const year = Math.max(yearOf(date), first);
  return leaveType === 'personal' ? Math.min(year, leave.current_year) : year;
};

/** What one accrual row earned for the given leave type. */
export const accrualHours = (row: LeaveAccrualRow, type: BalanceLeaveType): number =>
  type === 'personal' ? Number(row.annual_hours) : Number(row.sick_hours);

/** Blank or unparseable is null; the caller decides what that means. */
export const parseNumber = (text: string): number | null => {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : null;
};
